import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalFooter,
  Image,
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Flex,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider
} from '@chakra-ui/react';
import Cropper from 'react-easy-crop';
import CloseModalIcon from '../../../../assets/close-modal-icon.svg';
import Button from '../../../../components/Button';
import CardArticle from '../../../DiscoveryComponent/MiddleContentComponent/components/card-article.component';
import SmallWidgetArticleComponent from '../../../../routes/ArticleViewer/components/ArticleFooter/components/small-widget-article.component';
import PreviewArticleComponent from '../../../Lexical/ui/PreviewArticleComponent';
import { CATEGORY_LIST_ICON } from '../../../../constants/article.constant';
import { useAtom } from 'jotai';
import { articlePayloadAtom } from '../../atom';
import { profileAtom } from '../../../Settings/atom';
import debounce from 'lodash.debounce';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { normalizeDecimal } from '../../../../utils/helper.util';

function getCroppedImg(imageSrc, crop) {
  // Helper to crop the image using canvas
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const scale = image.naturalWidth / image.width;
      canvas.width = crop.width * scale;
      canvas.height = crop.height * scale;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        crop.x * scale,
        crop.y * scale,
        crop.width * scale,
        crop.height * scale,
        0,
        0,
        crop.width * scale,
        crop.height * scale
      );
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          resolve(blob);
        },
        'image/jpeg',
        0.95
      );
    };
    image.onerror = (e) => reject(e);
  });
}

const CROPPER_WIDTH_SIZE = 728;
const CROPPER_HEIGHT_SIZE = 400;

export default function EditArticleThumbnail(props) {
  const {
    isOpen,
    onClose,
    file,
    onSaveCroppedImage,
    initialCrop,
    initialZoom,
    initialCroppedAreaPixels
  } = props;
  const [imageUrl, setImageUrl] = useState(null);
  const [crop, setCrop] = useState(initialCrop || { x: 0, y: 0 });
  const [zoom, setZoom] = useState(initialZoom || 1); // Will be updated to fitImageZoom when image loads
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(initialCroppedAreaPixels || null);
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [originalImageDimensions, setOriginalImageDimensions] = useState({ width: 0, height: 0 });
  const [fitImageZoom, setFitImageZoom] = useState(1);
  const lastCrop = useRef(crop);

  const [articlePayload] = useAtom(articlePayloadAtom);
  const [profile] = useAtom(profileAtom);
  const [isChanged, setIsChanged] = useState(false);

  // Debounced function to update previewUrl
  const updatePreview = useCallback(
    debounce(async (imageUrl, croppedAreaPixels, zoom) => {
      if (!imageUrl || !croppedAreaPixels) return;
      try {
        const croppedBlob = await getCroppedImg(imageUrl, croppedAreaPixels, zoom);
        const url = URL.createObjectURL(croppedBlob);
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch {
        // Ignore preview errors
      }
    }, 200),
    []
  );

  const onCropComplete = useCallback(
    (croppedArea, croppedAreaPixels) => {
      setCroppedAreaPixels(croppedAreaPixels);
      updatePreview(imageUrl, croppedAreaPixels, zoom);
    },
    [zoom, updatePreview]
  );

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setImageUrl(url);
      setPreviewUrl(null);

      const img = new window.Image();
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        setOriginalImageDimensions({ width, height });

        const normalizeDecimalWidth = normalizeDecimal(CROPPER_WIDTH_SIZE / width);
        const normalizeDecimalHeight = normalizeDecimal(CROPPER_HEIGHT_SIZE / height);
        const zoomToCover =
          width < CROPPER_WIDTH_SIZE && height < CROPPER_HEIGHT_SIZE
            ? Math.min(normalizeDecimalHeight, normalizeDecimalWidth)
            : 1;

        setFitImageZoom(zoomToCover);

        setTimeout(() => {
          // If initial values are provided, use them, else use fit/center
          setZoom(initialZoom || zoomToCover);
          setTimeout(() => {
            setCrop(initialCrop || { x: 0, y: 0 });
            // If initial cropped area is provided, set it and generate preview
            if (initialCroppedAreaPixels) {
              setCroppedAreaPixels(initialCroppedAreaPixels);
            } else {
              setCroppedAreaPixels(null);
            }
          }, 100);
        }, 300);
      };
      img.src = url;

      return () => {
        URL.revokeObjectURL(url);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
      };
    }
  }, [file, initialCrop, initialZoom, initialCroppedAreaPixels]);

  // Function to fit entire image
  const fitEntireImage = () => {
    const targetZoom = fitImageZoom;
    const currentZoom = zoom;

    // If already very close to target, just center and set zoom
    if (Math.abs(targetZoom - currentZoom) < 0.01) {
      setCrop({ x: 0, y: 0 });
      setZoom(targetZoom);
      return;
    }

    // Smooth transition animation
    const steps = 15;
    const zoomStep = (targetZoom - currentZoom) / steps;

    let step = 1;
    const animateZoom = () => {
      if (step <= steps) {
        setZoom(currentZoom + zoomStep * step);
        step++;
        setTimeout(animateZoom, 16); // ~60fps
      } else {
        setZoom(targetZoom); // Ensure we end exactly at target
        // Final centering after zoom is complete with multiple attempts for precision
        setTimeout(() => {
          setCrop({ x: 0, y: 0 });
          // Additional micro-adjustment for perfect alignment
          setTimeout(() => {
            setCrop({ x: 0, y: 0 });
          }, 50);
        }, 50);
      }
    };

    // Start with centering
    setCrop({ x: 0, y: 0 });
    animateZoom();
  };

  const handleSave = async () => {
    if (!imageUrl) return;

    // Use existing crop area or create a default one based on current crop and zoom
    let area = croppedAreaPixels || initialCroppedAreaPixels;

    // If no area exists, create a default crop area based on current crop position and zoom
    if (!area) {
      const scale = 1 / zoom;

      area = {
        x: crop.x * scale,
        y: crop.y * scale,
        width: CROPPER_WIDTH_SIZE * scale,
        height: CROPPER_HEIGHT_SIZE * scale
      };
    }

    setLoading(true);
    try {
      // Crop the image
      const croppedBlob = await getCroppedImg(imageUrl, area, zoom);
      // Create a preview URL for the UI
      const previewUrl = URL.createObjectURL(croppedBlob);
      // Pass the blob and preview URL to parent
      onSaveCroppedImage({
        previewUrl,
        croppedBlob,
        originalFile: file,
        crop,
        zoom,
        croppedAreaPixels: area
      });
    } catch {
      alert('Failed to crop image');
    } finally {
      setLoading(false);
      onClose();
    }
  };

  // Calculate if image needs padding
  const needsPadding = useMemo(() => {
    if (!originalImageDimensions.width || !originalImageDimensions.height) return false;
    const imageAspectRatio = originalImageDimensions.width / originalImageDimensions.height;
    const targetAspectRatio = 16 / 9;
    return Math.abs(imageAspectRatio - targetAspectRatio) > 0.01;
  }, [originalImageDimensions]);

  const handleCloseModal = () => {
    setIsChanged(false);
    onClose?.();
  };

  const handleZoomChange = (value) => {
    setIsChanged(true);
    setZoom(value);
  };

  const handleCropChange = (newCrop) => {
    const isMoving = newCrop.x !== lastCrop.current.x || newCrop.y !== lastCrop.current.y;
    if (isMoving) setIsChanged(true);

    lastCrop.current = newCrop;
    setCrop(newCrop);
  };

  return (
    <Modal isOpen={isOpen} isCentered onClose={handleCloseModal} size="xl">
      <ModalOverlay backgroundColor={'rgba(0, 0, 0, 0.5)'} />
      <ModalContent
        px={'15px'}
        py={5}
        bgColor={'#FAFAFA'}
        rounded={'12px'}
        maxW={{ base: '480px', md: '760px' }}
      >
        <ModalHeader p={0} w={'100%'} m={0} maxWidth={'unset'}>
          <div className="flex w-full items-center justify-between pb-2 border-b border-earth-brown-50">
            <p className="text-lg font-[800]">Edit Cover Thumbnail</p>
            <button className="w-10 h-10 flex items-center justify-center" onClick={onClose}>
              <Image className="cursor-pointer" src={CloseModalIcon} alt="Close" />
            </button>
          </div>
        </ModalHeader>
        <ModalBody py={6} px={0}>
          <div className="flex flex-col items-center gap-5">
            {/* Smart Cropping Info */}
            <Alert status="info" bg="blue.50" border="1px solid" borderColor="blue.200">
              <AlertIcon color="blue.500" />
              <Box>
                <AlertTitle fontSize="sm" color="blue.800">
                  Smart Thumbnail Processing
                </AlertTitle>
                <AlertDescription fontSize="xs" color="blue.700">
                  {needsPadding
                    ? "Your image will be fitted completely with smart padding - no content cut off! Use 'Fit Entire Image' to see the full image."
                    : "Your image already has a 16:9 aspect ratio - perfect for thumbnails! 'Fit Entire Image' shows the complete view."}
                </AlertDescription>
              </Box>
            </Alert>

            <Box width="100%" rounded="xl" border={'1px solid #E6E7EB'}>
              <Box
                position="relative"
                width={CROPPER_WIDTH_SIZE}
                height={CROPPER_HEIGHT_SIZE}
                bg="#222"
                roundedTop="xl"
                overflow="hidden"
              >
                {imageUrl && (
                  <Cropper
                    image={imageUrl}
                    crop={crop}
                    zoom={zoom}
                    onCropChange={handleCropChange}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    cropSize={{ width: CROPPER_WIDTH_SIZE, height: CROPPER_HEIGHT_SIZE }}
                    cropShape="rect"
                    showGrid={true}
                    restrictPosition={false} // Allow user to drag crop area
                    objectFit="contain" // Ensure entire image is visible by default
                    zoomWithScroll={false}
                  />
                )}
              </Box>

              <Box className="mx-auto my-5" width="100%">
                <Flex flexDir="row" justifyContent="center" alignItems="center" gap={5}>
                  <ZoomOut />
                  <Box position="relative" width="50%">
                    <Slider
                      aria-label="Zoom"
                      value={zoom}
                      min={0.1} // Allow zooming out much further
                      max={Math.max(8, fitImageZoom)}
                      step={0.01}
                      onChange={handleZoomChange}
                    >
                      <SliderTrack bg="gray.200" borderRadius="full" h="6px">
                        <SliderFilledTrack bg="#4CAF50" />
                      </SliderTrack>
                      <SliderThumb />
                    </Slider>
                    {/* Fit entire image indicator */}
                    <Box
                      position="absolute"
                      top="50%"
                      transform="translateY(-50%)"
                      left={`${((fitImageZoom - 0.1) / (Math.max(8, fitImageZoom) - 0.1)) * 100}%`}
                      width="3px"
                      height="12px"
                      bg="orange.400"
                      borderRadius="full"
                      zIndex={1}
                      pointerEvents="none"
                      _before={{
                        content: '""',
                        position: 'absolute',
                        top: '-2px',
                        left: '-1px',
                        width: '5px',
                        height: '16px',
                        bg: 'orange.500',
                        borderRadius: 'full',
                        opacity: Math.abs(zoom - fitImageZoom) < 0.05 ? 1 : 0.7
                      }}
                    />
                  </Box>
                  <ZoomIn />
                </Flex>

                {/* Fit Entire Image Button */}
                <div className="flex justify-center mt-3 mb-4">
                  <button
                    onClick={fitEntireImage}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-2 shadow-sm ${
                      Math.abs(zoom - fitImageZoom) < 0.05
                        ? 'bg-green-500 hover:bg-green-600 text-white'
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    {Math.abs(zoom - fitImageZoom) < 0.05 ? '✓ Perfect Fit' : 'Fit Entire Image'}
                  </button>
                </div>
              </Box>
            </Box>

            <Box className="w-full">
              <Box>
                <p className="text-2xl font-[800] text-center">Preview Across Media</p>
                <p className="text-base font-[400] text-center text-02-dim-gray">
                  See how your image will appear in different layouts across the platform.
                </p>
              </Box>
              <Tabs index={tabIndex} onChange={setTabIndex} width="100%" mt={5}>
                <TabList justifyContent="space-around" width="100%">
                  {['Feed Card', 'Square Thumbnail', 'Small Widget', 'Article Hero'].map((tab) => (
                    <Tab
                      key={tab}
                      flex="1"
                      textAlign="center"
                      minW="auto"
                      className="font-[800] test-base"
                      color="#808080"
                      borderBottom={'2px solid #CCCCCC'}
                      _selected={{
                        color: '#4CAF50',
                        borderBottom: '2px solid #4CAF50'
                      }}
                      whiteSpace={'nowrap'}
                    >
                      {tab}
                    </Tab>
                  ))}
                </TabList>
                <Box position="relative">
                  <TabPanels>
                    <TabPanel>
                      <Box
                        paddingX={{ base: '0px', md: '20px' }}
                        paddingY={'20px'}
                        border={'1px solid #E6E7EB'}
                        borderRadius={'12px'}
                      >
                        <CardArticle
                          item={{
                            ...articlePayload,
                            cover: previewUrl || articlePayload.cover,
                            creator: profile,
                            nameCreator: profile?.firstName || 'Anonymous',
                            imgUrl: profile?.imgUrl,
                            writingType: CATEGORY_LIST_ICON[0],
                            topics: articlePayload?.topics || [],
                            reactionNumber: 0,
                            commentNumber: 0,
                            createdAt: new Date(),
                            readLength: '5 min',
                            reputationPoint: { amount: 0 }
                          }}
                          index={0}
                          isPreview={true}
                          isHiddenFeaturedArticles={false}
                        />
                      </Box>
                    </TabPanel>
                    <TabPanel>
                      <Box
                        paddingX={{ base: '0px', md: '60px' }}
                        paddingY={'20px'}
                        border={'1px solid #E6E7EB'}
                        borderRadius={'12px'}
                      >
                        <CardArticle
                          item={{
                            ...articlePayload,
                            cover: previewUrl || articlePayload.cover,
                            creator: profile,
                            nameCreator: profile?.firstName || 'Anonymous',
                            imgUrl: profile?.imgUrl,
                            writingType: CATEGORY_LIST_ICON[0],
                            topics: articlePayload?.topics || [],
                            reactionNumber: 0,
                            commentNumber: 0,
                            createdAt: new Date(),
                            readLength: '5 min',
                            reputationPoint: { amount: 0 }
                          }}
                          index={0}
                          isPreview={true}
                          isHiddenFeaturedArticles={true}
                        />
                      </Box>
                    </TabPanel>
                    <TabPanel>
                      <Box
                        paddingX={{ base: '0px', md: '50px' }}
                        paddingY={'20px'}
                        border={'1px solid #E6E7EB'}
                        borderRadius={'12px'}
                      >
                        <SmallWidgetArticleComponent
                          key={articlePayload?.id}
                          article={{
                            ...articlePayload,
                            cover: previewUrl || articlePayload.cover,
                            creator: profile
                          }}
                          profile={profile}
                        />
                      </Box>
                    </TabPanel>
                    <TabPanel>
                      <Box
                        paddingX={{ base: '0px', md: '20px' }}
                        paddingY={'20px'}
                        border={'1px solid #E6E7EB'}
                        borderRadius={'12px'}
                      >
                        <PreviewArticleComponent
                          cover={previewUrl || articlePayload.cover}
                          disableTabsContent={true}
                        />
                      </Box>
                    </TabPanel>
                  </TabPanels>
                  <Divider my={2} borderColor={'#D1C8C4'} />
                  {/* Overlay to block pointer events */}
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    zIndex={10}
                    background="transparent"
                    pointerEvents="all"
                  />
                </Box>
              </Tabs>
            </Box>
          </div>
        </ModalBody>
        <ModalFooter w="100%" p="0" gap="2">
          <Button
            className="w-fit h-[42px] font-[900]"
            onClick={onClose}
            isLoading={loading}
            disabled={loading}
            variant="outline"
          >
            Discard
          </Button>
          <Button
            className="w-fit h-[42px] font-[900]"
            onClick={handleSave}
            isLoading={loading}
            disabled={loading || !imageUrl || !isChanged}
          >
            Save Crop
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
